import React, { useState, useEffect } from 'react';
import { X, Play, Square, ChevronRight, Phone } from 'lucide-react';
import { AVAILABLE_LIVE_VOICES } from '@/app/lib/types/live_voice';
import { useCharacterData, useGlobalChatState } from '../../hooks/useGlobalChatState';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import { CallPerMessagePrice, LiveChatPerMessagePrice } from '../../data/chatSetting/settingOptions';
import PopupTemplate from '@/app/ui/popupTemplate';
import { Button } from '@/components/ui/button';
import InsufficientCreditPopup from '@/app/popup/InsufficientCreditPopup';
import { useTranslations } from 'next-intl';
import '@/app/ui/scrollbar.css';

interface VoiceEditPopupProps {
  isOpen: boolean;
  onClose: () => void;
  showPricing?: boolean;
  onCallButtonClicked?: () => void;
  onStartChat?: () => void;
}

export function LiveVoiceEditPopup({ isOpen, onClose, showPricing = true, onCallButtonClicked, onStartChat }: VoiceEditPopupProps) {
  const t = useTranslations('chat');
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>('');
  const [voiceGenderFilter, setVoiceGenderFilter] = useState<string>('female');
  const [isPlaying, setIsPlaying] = useState<string | null>(null);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showInsufficientCreditPopup, setShowInsufficientCreditPopup] = useState(false);

  const { userEmail } = useAuth();
  const { character } = useCharacterData();
  const [globalChatStateData, globalActions] = useGlobalChatState();
  const [userAppDataState] = useUserAppData(userEmail || '');

  // Filter voices based on selected gender
  const filteredVoices = AVAILABLE_LIVE_VOICES.filter(voice => voice.gender === voiceGenderFilter);

  // Set initial voice selection when popup opens - start in preview mode
  useEffect(() => {
    if (isOpen) {
      setIsEditMode(false); // Always start in preview mode

      const currentTtsId = globalChatStateData.userChatSetting.ttsId;
      // First try to find the stored voice in available live voices
      const storedVoice = currentTtsId ? AVAILABLE_LIVE_VOICES.find(v => v.id === currentTtsId) : null;

      let voiceToSet: string;
      if (storedVoice) {
        // Use the stored voice and set the gender filter to match
        voiceToSet = currentTtsId;
        setVoiceGenderFilter(storedVoice.gender);
      } else {
        // If no stored voice or it doesn't exist, use the first available voice as default
        const firstVoice = AVAILABLE_LIVE_VOICES.find(v => v.gender === voiceGenderFilter) || AVAILABLE_LIVE_VOICES[0];
        voiceToSet = firstVoice?.id || '';
        if (firstVoice) {
          setVoiceGenderFilter(firstVoice.gender);
        }
      }
      
      if (voiceToSet) {
        setSelectedVoiceId(voiceToSet);
        // Always update globalChatStateData with the voice (even if it's the same or default)
        globalActions.setTtsId(voiceToSet);
      }
    }
  }, [isOpen, globalChatStateData.userChatSetting.ttsId]);

  // Update selected voice when gender filter changes (only when no stored voice exists)
  useEffect(() => {
    if (isOpen && isEditMode) {
      const currentTtsId = globalChatStateData.userChatSetting.ttsId;
      const storedVoice = currentTtsId ? AVAILABLE_LIVE_VOICES.find(v => v.id === currentTtsId) : null;

      // Only auto-select if there's no stored voice, otherwise keep current selection
      if (!storedVoice || storedVoice.gender !== voiceGenderFilter) {
        const firstVoice = AVAILABLE_LIVE_VOICES.find(v => v.gender === voiceGenderFilter);
        if (firstVoice) {
          setSelectedVoiceId(firstVoice.id);
          // Update globalChatStateData immediately when voice changes
          globalActions.setTtsId(firstVoice.id);
        }
      }
    }
  }, [voiceGenderFilter, isOpen, isEditMode, globalChatStateData.userChatSetting.ttsId]);

  // Always update globalChatStateData whenever selectedVoiceId changes
  useEffect(() => {
    if (selectedVoiceId && isOpen) {
      globalActions.setTtsId(selectedVoiceId);
    }
  }, [selectedVoiceId, isOpen]);

  // Cleanup audio when popup closes
  useEffect(() => {
    if (!isOpen && currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
      setIsPlaying(null);
    }
  }, [isOpen, currentAudio]);

  const handlePlayAudio = (voiceId: string) => {
    // Stop any currently playing audio
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }

    if (isPlaying === voiceId) {
      setIsPlaying(null);
      setCurrentAudio(null);
      return;
    }

    setIsPlaying(voiceId);
    const voice = AVAILABLE_LIVE_VOICES.find(v => v.id === voiceId);
    if (!voice) return;

    const audio = new Audio(voice.audio_url);
    setCurrentAudio(audio);

    audio.onended = () => {
      setIsPlaying(null);
      setCurrentAudio(null);
    };

    audio.onerror = () => {
      console.error('Error playing audio');
      setIsPlaying(null);
      setCurrentAudio(null);
    };

    audio.play().catch(error => {
      console.error('Error playing audio:', error);
      setIsPlaying(null);
      setCurrentAudio(null);
    });
  };

  const handleStopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    setIsPlaying(null);
  };

  const handleSaveToLocal = async () => {
    // Ensure we have a valid voice ID (use default if none selected)
    const voiceIdToSave = selectedVoiceId || AVAILABLE_LIVE_VOICES[0]?.id || '';
    
    if (voiceIdToSave) {
      console.log('Saving voice to global state:', {
        selectedVoiceId: voiceIdToSave,
        voiceName: AVAILABLE_LIVE_VOICES.find(v => v.id === voiceIdToSave)?.name
      });

      // Update global chat state (this is now permanent)
      globalActions.setTtsId(voiceIdToSave);
      
      // Also update local state if it was empty
      if (!selectedVoiceId) {
        setSelectedVoiceId(voiceIdToSave);
      }
    }
  };

  const handleSaveAndClose = async () => {
    if (!selectedVoiceId || isApplying) {
      console.log('Save voice validation failed:', {
        selectedVoiceId,
        isSaving: isApplying
      });
      return;
    }

    console.log('Attempting to save voice:', {
      selectedVoiceId,
      voiceName: AVAILABLE_LIVE_VOICES.find(v => v.id === selectedVoiceId)?.name
    });

    setIsApplying(true);

    // Update global chat state first (this is now permanent)
    globalActions.setTtsId(selectedVoiceId);

    setIsApplying(false);
    setIsEditMode(false); // Go back to preview mode instead of closing
  };

  // Get current voice for preview mode
  const currentVoice = AVAILABLE_LIVE_VOICES.find(v => v.id === globalChatStateData.userChatSetting.ttsId) ||
                      AVAILABLE_LIVE_VOICES.find(v => v.id === selectedVoiceId) ||
                      AVAILABLE_LIVE_VOICES[0];

  const handleCallButtonClick = () => {
    // Check if user has enough credits for at least one voice message
    const totalCredits = userAppDataState.creditCount + userAppDataState.dailyCreditCount;
    if (totalCredits < CallPerMessagePrice) {
      console.log("💰 Insufficient credits for voice chat, showing popup...");
      setShowInsufficientCreditPopup(true);
      return;
    }
    if (onCallButtonClicked) {
      onCallButtonClicked();
    }
    onClose(); // Close the popup
  };

  const handleStartLiveButtonClick = async () => {
    // Check if user has enough credits for at least one voice message
    const totalCredits = userAppDataState.creditCount + userAppDataState.dailyCreditCount;
    if (totalCredits < LiveChatPerMessagePrice) {
      console.log("💰 Insufficient credits for live chat, showing popup...");
      setShowInsufficientCreditPopup(true);
      return;
    }

    // set active image to default
    await globalActions.setActiveImage(globalChatStateData.character?.metadata.image_uri || '');

    if (onStartChat) {
      // onCallButtonClicked(); // Then trigger the call
      onStartChat?.();
    }
    onClose(); // Close the popup
  };

  return (
    <PopupTemplate
      isOpen={isOpen}
      onClose={onClose}
      closeable={true}
      isTransparent={false}
      className="max-h-[65vh] flex flex-col overflow-hidden"
    >
      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto dark-scrollbar pt-4 px-8 pb-4 space-y-4">
        {isEditMode ? (
          <>
            {/* Select Voice Title */}
            <div className="text-center">
              <h3 className="text-white text-lg font-semibold font-['Montserrat',Helvetica]">{t('selectVoice')}</h3>
            </div>

            {/* Gender Filter */}
            <div>
              <div className="space-y-2 mb-4">
                <label className="block text-sm font-medium text-white font-['Montserrat',Helvetica]">{t('filterByGender')}</label>
                <select
                  value={voiceGenderFilter}
                  onChange={(e) => setVoiceGenderFilter(e.target.value)}
                  className="w-32 px-3 py-2 bg-[#2a2a2e] rounded-xl border border-[#3a3a3e] text-white text-sm focus:border-[#5856d6] focus:outline-none transition-colors appearance-none font-['Montserrat',Helvetica]"
                  style={{
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px',
                    paddingRight: '40px'
                  }}
                >
                  <option value="female" className="bg-black">{t('female')}</option>
                  <option value="male" className="bg-black">{t('male')}</option>
                </select>
              </div>
            </div>

            {/* Voice Options */}
            <div className="space-y-3">
              {filteredVoices.map((voice) => (
                <div
                  key={voice.id}
                  onClick={() => {
                    console.log('Voice selected:', {
                      voiceId: voice.id,
                      voiceName: voice.name
                    });
                    setSelectedVoiceId(voice.id);
                    // Update globalChatStateData immediately when user selects a voice
                    globalActions.setTtsId(voice.id);
                  }}
                  className={`w-full py-2.5 px-4 rounded-xl font-medium text-sm transition-all duration-200 font-['Montserrat',Helvetica] cursor-pointer ${
                    selectedVoiceId === voice.id
                      ? "bg-[#5856d6] text-white border-2 border-[#5856d6]"
                      : "bg-[#2a2a2e] text-gray-300 hover:bg-[#3a3a3e] border-2 border-transparent"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isPlaying === voice.id) {
                          handleStopAudio();
                        } else {
                          handlePlayAudio(voice.id);
                        }
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors flex-shrink-0"
                    >
                      {isPlaying === voice.id ? (
                        <Square className="w-3 h-3" />
                      ) : (
                        <Play className="w-3 h-3 ml-0.5" />
                      )}
                    </button>
                    <div className="text-white">
                      {voice.name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          /* Preview Mode */
          <div className="flex flex-col justify-center py-8">
            {/* Voice Preview */}
            <span className="text-white text-left text-sm font-medium font-['Montserrat',Helvetica] mb-2">{t('characterWillSpeakWithThisVoice')}</span>
            <div
              onClick={() => setIsEditMode(true)}
              className="w-full max-w-md py-2 px-6 mb-6 rounded-xl font-medium text-sm transition-all duration-200 font-['Montserrat',Helvetica] cursor-pointer bg-[#2a2a2e] text-gray-300 hover:bg-[#3a3a3e] border-2 border-transparent"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isPlaying === currentVoice.id) {
                        handleStopAudio();
                      } else {
                        handlePlayAudio(currentVoice.id);
                      }
                    }}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors flex-shrink-0"
                  >
                    {isPlaying === currentVoice.id ? (
                      <Square className="w-3 h-3" />
                    ) : (
                      <Play className="w-3 h-3 ml-0.5" />
                    )}
                  </button>
                  <div className="text-gray-300">
                    {currentVoice.name}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </div>

            {/* Separator */}
            <div className="w-full max-w-md h-px bg-gray-600 mb-6"></div>

            {/* Live Chat Button */}
            {(globalChatStateData.character?.metadata.head_confidence != null &&
            globalChatStateData.character?.metadata.head_confidence > 0.5) && (
              <button
                onClick={handleStartLiveButtonClick}
                className="flex items-center justify-center gap-3 w-full max-w-md py-4 px-6 mb-4 rounded-xl font-medium text-lg transition-all duration-200 font-['Montserrat',Helvetica] cursor-pointer bg-[#5856d6] text-white hover:bg-[#4a46c6] border-2 border-[#5856d6] hover:border-[#4a46c6]"
              >
                <Play className="w-6 h-6" />
                <span>{t('liveChat')}</span>
              </button>
            )}

            {/* Voice Chat Button */}
            <button
              onClick={handleCallButtonClick}
              className="flex items-center justify-center gap-3 w-full max-w-md py-2 px-6 mb-6 rounded-xl font-medium text-lg transition-all duration-200 font-['Montserrat',Helvetica] cursor-pointer bg-[#2a2a2e] text-gray-300 hover:bg-[#3a3a3e] border-2 border-transparent hover:border-gray-600"
            >
              <Phone className="w-6 h-6" />
              <span>{t('voiceChat')}</span>
            </button>

            {/* Pricing */}
            {(globalChatStateData.character?.metadata.head_confidence != null &&
             globalChatStateData.character?.metadata.head_confidence > 0.5) && (
              <>
                <div className="text-gray-400 text-xs font-['Montserrat',Helvetica]">
                  {t('eachLiveChatMessageCosts', { price: LiveChatPerMessagePrice })}
                <span className="text-purple-300 font-bold text-xs mb-3">{t('fortyPercentOff')}</span>
                </div>
              </>
            )}
            <div className="text-gray-400 text-xs font-['Montserrat',Helvetica]">
              {t('eachVoiceChatMessageCosts', { price: CallPerMessagePrice })}
            </div>
          </div>
        )}
      </div>

      {/* Footer - Only show in edit mode */}
      {isEditMode && (
        <div className="flex-shrink-0">
          {/* Action Buttons */}
          <div className="flex gap-3 border-t border-white/10 p-4">
            <Button
              variant="ghost"
              className="flex-1 text-white hover:bg-[#2a2a2e] rounded-xl"
              onClick={() => setIsEditMode(false)}
            >
              {t('back')}
            </Button>
            <Button
              className="flex-1 bg-[#5856d6] hover:bg-[#5856d6] text-white rounded-xl"
              onClick={handleSaveAndClose}
              disabled={!selectedVoiceId || isApplying}
            >
              {isApplying ? t('applying') : t('useVoice')}
            </Button>
          </div>
        </div>
      )}

      {/* Insufficient Credit Popup */}
      <InsufficientCreditPopup
        isOpen={showInsufficientCreditPopup}
        onClose={() => setShowInsufficientCreditPopup(false)}
      />
    </PopupTemplate>
  );
} 